import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import bs58 from 'bs58';
import { _setDiagState, runDiag } from '../utils/authDiag';
import { setAnalyticsContext, trackEvent } from '../utils/analytics';
import { identifyUser, resetPostHog } from '../utils/posthog';
import { apiFetch, getApiHost } from '../utils/api';

const API_BASE_URL = getApiHost();

// User interface matching API response
export type TierName = 'free' | 'watcher' | 'deep_diver';

export interface DnetBreakdown {
  held: number;
  staked: number;
  total: number;
}

export interface UserSubscription {
  tier: TierName;
  status: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd?: boolean;
  cancelAt?: string | null;
}

export interface UserCryptoSubscription {
  tier: TierName;
  currentPeriodEnd: string;
  paidAt: string;
  asset: 'USDC';
  active: boolean;
}

export interface User {
  id: string;
  walletAddress: string | null;
  email: string | null;
  username: string | null;
  credits: number;
  createdAt: string;
  lastLoginAt?: string;
  tier?: TierName;
  dnetBalance?: number;
  dnetBreakdown?: DnetBreakdown;
  subscription?: UserSubscription;
  cryptoSubscription?: UserCryptoSubscription;
  dnetTier?: TierName;  // tier from DNET holdings alone (before subscription boost)
  passkeyCount?: number;
}

interface AuthContextType {
  // User state
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Auth methods
  loginWithWallet: () => Promise<void>;
  loginWithSignedMessage: (
    walletAddress: string,
    message: string,
    signatureBytes: Uint8Array,
    analyticsMethod?: string,
  ) => Promise<void>;
  loginWithEmail: (email: string) => Promise<{ success: boolean; message: string }>;
  verifyEmailToken: (token: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;

  // Account linking
  linkWallet: () => Promise<void>;
  linkEmail: (email: string) => Promise<void>;

  // Login modal control. Prefer openLogin(source) — it attributes the prompt.
  // setShowLoginModal stays for closing, and for the rare caller that has
  // already tracked its own source.
  showLoginModal: boolean;
  setShowLoginModal: (show: boolean) => void;
  openLogin: (source: string, redirectTo?: string | null, props?: Record<string, unknown>) => void;

  // Where to navigate after a successful login. Set by GatedLink/etc.
  // when the user clicks a protected link while logged out.
  pendingLoginRedirect: string | null;
  requestLogin: (redirectTo?: string | null) => void;
  clearPendingLoginRedirect: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [pendingLoginRedirect, setPendingLoginRedirect] = useState<string | null>(null);

  // Single attributed entry point for opening the login modal. Previously each
  // caller had to remember to fire login_prompt_clicked itself, and most didn't:
  // 422 users dismissed the modal against only 276 recorded opening it, so ~146
  // people entered the funnel through a step we never logged. Route every opener
  // through here so a new call site cannot be added untracked.
  const openLogin = useCallback((
    source: string,
    redirectTo?: string | null,
    props?: Record<string, unknown>,
  ) => {
    trackEvent('login_prompt_clicked', { source, ...(props || {}) });
    setPendingLoginRedirect(redirectTo ?? null);
    setShowLoginModal(true);
  }, []);

  const requestLogin = useCallback((redirectTo?: string | null) => {
    openLogin('gated_link', redirectTo);
  }, [openLogin]);
  const clearPendingLoginRedirect = useCallback(() => setPendingLoginRedirect(null), []);

  const { publicKey, signMessage, connected, connecting, disconnect, wallet, connect } = useWallet();
  const { visible: walletModalVisible } = useWalletModal();

  // WalletModal (from @solana/wallet-adapter-react-ui) only calls
  // select(name) on pick — it relies on WalletProvider's autoConnect
  // to actually open the wallet. We deliberately keep autoConnect off
  // (App.tsx) so visitors don't get a Phantom unlock prompt on every
  // page load, so we have to drive connect() ourselves when the modal
  // closes. Opening the modal is itself the user-intent signal — the
  // page-load case never opens it, so this never fires uninvited.
  const prevModalVisible = React.useRef(false);
  useEffect(() => {
    if (prevModalVisible.current && !walletModalVisible) {
      if (wallet && !connected && !connecting) {
        connect().catch((err) => {
          console.warn('Wallet connect after modal close failed:', err);
        });
      }
    }
    prevModalVisible.current = walletModalVisible;
  }, [walletModalVisible, wallet, connected, connecting, connect]);

  // Get stored JWT token
  const getToken = (): string | null => {
    return localStorage.getItem('deepnets_token');
  };

  // Store JWT token
  const setToken = (token: string): void => {
    localStorage.setItem('deepnets_token', token);
  };

  // Clear JWT token
  const clearToken = (): void => {
    localStorage.removeItem('deepnets_token');
  };

  // Create auth headers
  const getAuthHeaders = (): HeadersInit => {
    const token = getToken();
    return token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
  };

  // Refresh user data from API
  const refreshUser = useCallback(async (): Promise<void> => {
    const token = getToken();
    if (!token) {
      setUser(null);
      setIsLoading(false);
      return;
    }

    try {
      const response = await apiFetch(`${API_BASE_URL}/api/auth/me?bustCache=true`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
      } else {
        // Token is invalid or expired — clean up fully
        clearToken();
        setUser(null);
      }
    } catch (error) {
      console.error('Error refreshing user:', error);
      // Don't clear token on network error, user might just be offline
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh user on mount
  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  // Auto-sign when wallet connects and user is not authenticated
  // Use a ref to prevent re-firing on every state change
  const autoSignAttempted = React.useRef(false);

  useEffect(() => {
    // Reset when wallet disconnects so next connect can auto-sign
    if (!connected) {
      autoSignAttempted.current = false;
      return;
    }

    // When wallet connects and we have a token, make sure user state is hydrated
    if (connected && !isLoading && !user) {
      const token = getToken();
      if (token) {
        refreshUser();
        return;
      }
    }

    // Auto-sign: wallet connected, no token, no user — trigger sign-in
    // Skip if login modal is open (modal handles its own sign-in flow)
    if (connected && publicKey && signMessage && !isLoading && !user && !autoSignAttempted.current && !loggingOut.current && !showLoginModal) {
      const token = getToken();
      if (token) return;

      autoSignAttempted.current = true;
      performWalletLogin(publicKey.toBase58(), signMessage).catch(() => {
        autoSignAttempted.current = false;
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, publicKey, signMessage, user, isLoading, showLoginModal]);

  // Core wallet login logic — used by both auto-sign and manual login,
  // plus the Jupiter Mobile WalletConnect flow (which calls in via
  // `loginWithExternalSigner` below with its own signing function).
  const performWalletLogin = async (
    walletAddress: string,
    signer: (message: Uint8Array) => Promise<Uint8Array>,
    analyticsMethod: string = 'wallet',
  ): Promise<void> => {
    const timestamp = new Date().toISOString();
    const message = `Sign in to deepnets.ai\nTimestamp: ${timestamp}`;
    const messageBytes = new TextEncoder().encode(message);

    const signatureBytes = await signer(messageBytes);
    const signature = bs58.encode(signatureBytes);

    const response = await apiFetch(`${API_BASE_URL}/api/auth/wallet`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ walletAddress, signature, message }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Login failed' }));
      trackEvent('login_failed', { method: analyticsMethod, status: response.status, reason: String(error.error || 'Login failed').slice(0, 80) });
      throw new Error(error.error || 'Login failed');
    }

    const data = await response.json();
    setToken(data.token);
    setUser(data.user);
    setShowLoginModal(false);
    trackEvent('login_completed', { method: analyticsMethod });
    // /api/auth/wallet returns the lean publicUser shape (no tier,
    // subscription, dnetBalance). Pull the resolved view so the UI
    // shows the right tier without waiting for a manual refresh.
    refreshUser();
  };

  // For callers that have already produced a signed message externally
  // — Jupiter Mobile via WalletConnect (our jupiterMobileSignIn.ts),
  // Phantom Mobile via the deeplink protocol, etc. The wallet's own
  // sign flow returned both walletAddress AND signature; this hook
  // hands them off to /api/auth/wallet without going through the
  // wallet-adapter signing interface.
  const loginWithSignedMessage = useCallback(
    async (
      walletAddress: string,
      message: string,
      signatureBytes: Uint8Array,
      analyticsMethod: string = 'wallet',
    ): Promise<void> => {
      const signature = bs58.encode(signatureBytes);
      const response = await apiFetch(`${API_BASE_URL}/api/auth/wallet`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ walletAddress, signature, message }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        trackEvent('login_failed', { method: analyticsMethod, status: response.status, reason: String(err.error || 'Login failed').slice(0, 80) });
        throw new Error(err.error || 'Login failed');
      }
      const data = await response.json();
      setToken(data.token);
      setUser(data.user);
      setShowLoginModal(false);
      trackEvent('login_completed', { method: analyticsMethod });
      // If the user just signed in with a wallet that doesn't match
      // a cached Phantom deeplink session, the cache is stale — wipe
      // it so subsequent in-app transactions don't try to silently
      // route through the wrong Phantom account. This handles the
      // "I signed in with Jupiter, why is it sending me to Phantom?"
      // case cleanly.
      import('../utils/phantomDeeplink')
        .then(m => {
          if (!m.persistentPhantomWalletMatches(walletAddress)) {
            m.clearPersistentPhantomSession();
          }
        })
        .catch(() => { /* best-effort */ });
      await refreshUser();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Login with Solana wallet (manual trigger)
  const loginWithWallet = useCallback(async (): Promise<void> => {
    if (!publicKey || !signMessage) {
      throw new Error('Wallet not connected');
    }

    try {
      await performWalletLogin(publicKey.toBase58(), signMessage);
    } catch (error) {
      console.error('Wallet login error:', error);
      // Covers the pre-request failures too: user rejected the signature prompt,
      // wallet locked, adapter error. Every one of these previously vanished.
      trackEvent('login_failed', { method: 'wallet', reason: (error instanceof Error ? error.message : 'unknown').slice(0, 80) });
      throw error;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicKey, signMessage]);

  // Request email magic link
  const loginWithEmail = async (email: string): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/auth/email/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send magic link');
      }

      return { success: true, message: data.message };
    } catch (error) {
      console.error('Email login error:', error);
      throw error;
    }
  };

  // Verify email token from magic link
  const verifyEmailToken = async (token: string): Promise<void> => {
    try {
      const response = await apiFetch(`${API_BASE_URL}/api/auth/email/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Token verification failed');
      }

      const data = await response.json();
      setToken(data.token);
      setUser(data.user);
      setShowLoginModal(false);
      trackEvent('login_completed', { method: 'email' });
      // /api/auth/email/verify returns the lean publicUser shape (no
      // tier, subscription, dnetBalance). Pull the resolved view so the
      // user lands on /profile with the right tier rendered immediately.
      await refreshUser();
    } catch (error) {
      console.error('Email verify error:', error);
      throw error;
    }
  };

  // Logout
  const loggingOut = React.useRef(false);

  const logout = useCallback((): void => {
    loggingOut.current = true;
    clearToken();
    setUser(null);
    autoSignAttempted.current = false;
    // Detach the PostHog person profile so subsequent anon events
    // don't get attributed to the logged-out user.
    resetPostHog();
    // Note: deliberately NOT clearing the persistent Phantom deeplink
    // session here. It's a "remember me" cache for the connect→sign
    // step pair, not auth state. If the user revokes deepnets.ai from
    // Phantom's side, the next signMessage will fail with an error
    // code and handlePhantomCallback wipes our cache automatically.
    // Logging out and back in shouldn't force the two-bounce flow
    // again — that defeats the persistence.
    //
    // The WalletConnect session IS cleared on logout though: WC
    // sessions actively occupy a "connected dApps" slot in the user's
    // wallet, and leaving them dangling after logout is more invasive
    // than the Phantom cache (which is purely local crypto state).
    import('../utils/walletConnectMobile')
      .then(m => m.clearWCSession())
      .catch(() => { /* best-effort */ });
    // Also disconnect wallet if connected
    if (connected) {
      disconnect().finally(() => {
        loggingOut.current = false;
      });
    } else {
      loggingOut.current = false;
    }
  }, [connected, disconnect]);

  // Link wallet to existing account
  const linkWallet = useCallback(async (): Promise<void> => {
    if (!publicKey || !signMessage) {
      throw new Error('Wallet not connected');
    }

    const token = getToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    const timestamp = new Date().toISOString();
    const message = `Link wallet to deepnets.ai account\nTimestamp: ${timestamp}`;
    const messageBytes = new TextEncoder().encode(message);

    try {
      const signatureBytes = await signMessage(messageBytes);
      const signature = bs58.encode(signatureBytes);
      const walletAddress = publicKey.toBase58();

      const response = await apiFetch(`${API_BASE_URL}/api/auth/link-wallet`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ walletAddress, signature, message }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to link wallet');
      }

      const data = await response.json();
      setToken(data.token);
      setUser(data.user);
    } catch (error) {
      console.error('Link wallet error:', error);
      throw error;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicKey, signMessage]);

  // Link email to existing account
  const linkEmail = async (email: string): Promise<void> => {
    const token = getToken();
    if (!token) {
      throw new Error('Not authenticated');
    }

    try {
      const response = await apiFetch(`${API_BASE_URL}/api/auth/link-email`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to link email');
      }

      const data = await response.json();
      setToken(data.token);
      setUser(data.user);
    } catch (error) {
      console.error('Link email error:', error);
      throw error;
    }
  };

  // Keep diagnostic utility in sync with React state & expose on window
  useEffect(() => {
    _setDiagState(
      { user, isAuthenticated: !!user, isLoading },
      { connected, publicKey: publicKey?.toBase58() ?? null },
    );
    (window as any).deepnetsDiag = runDiag;
  });

  // Push tier/dnetBalance into the analytics singleton so funnel events
  // can slice by user state at event time without a server round-trip.
  // Also identify to PostHog (no-op without a PostHog key) so the
  // person profile carries the same slice as server-side events.
  useEffect(() => {
    setAnalyticsContext({
      isAuthenticated: !!user,
      userTier: user?.tier,
      dnetTier: user?.dnetTier,
      dnetBalance: user?.dnetBalance,
    });
    if (user) {
      identifyUser({
        id: user.id,
        walletAddress: user.walletAddress,
        tier: user.tier,
        dnetTier: user.dnetTier,
        dnetBalance: user.dnetBalance,
      }).catch(() => { /* best-effort */ });
    }
  }, [user]);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    loginWithWallet,
    loginWithSignedMessage,
    openLogin,
    loginWithEmail,
    verifyEmailToken,
    logout,
    refreshUser,
    linkWallet,
    linkEmail,
    showLoginModal,
    setShowLoginModal,
    pendingLoginRedirect,
    requestLogin,
    clearPendingLoginRedirect,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
